import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilmCard from '@/components/feature/FilmCard';
import FilterBar from '@/pages/category/components/FilterBar';
import { films } from '@/mocks/films';

const PAGE_SIZE = 8;

const sentinels: Record<string, string> = {
  type: 'all',
  genre: '全部',
  region: '全部',
  year: '全部',
  sort: 'heat',
};

export default function CategoryPage() {
  const [params, setParams] = useSearchParams();
  const [mobileFilter, setMobileFilter] = useState(false);

  const type = params.get('type') ?? 'all';
  const genre = params.get('genre') ?? '全部';
  const region = params.get('region') ?? '全部';
  const year = params.get('year') ?? '全部';
  const sort = params.get('sort') ?? 'heat';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const titleType = type === 'movie' ? '电影' : type === 'series' ? '电视剧' : '全部影视';

  useEffect(() => {
    document.title = `${titleType}分类 - 姐姐的朋友影视内容站`;
  }, [titleType]);

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === sentinels[key]) next.delete(key);
      else next.set(key, value);
    });
    if (!('page' in patch)) next.delete('page');
    setParams(next);
  };

  const reset = () => setParams(new URLSearchParams());

  const filtered = useMemo(() => {
    const list = films.filter((f) => {
      if (type !== 'all' && f.type !== type) return false;
      if (genre !== '全部' && !f.genres.includes(genre)) return false;
      if (region !== '全部' && f.region !== region) return false;
      if (year !== '全部' && String(f.year) !== year) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'year') return b.year - a.year;
      return b.heat - a.heat;
    });
  }, [type, genre, region, year, sort]);

  const total = filtered.length;
  const visible = filtered.slice(0, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    setMobileFilter(false);
  }, [params]);

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="bg-primary-950">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${titleType}分类` }]} />
          <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50 mt-4 mb-3">
            {titleType}分类
          </h1>
          <p className="text-sm md:text-base text-background-200/80 max-w-2xl">
            浏览{titleType}合集，按题材、地区、年份与热度筛选，快速找到你想看的故事。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="hidden lg:block mb-8">
          <FilterBar
            type={type}
            genre={genre}
            region={region}
            year={year}
            sort={sort}
            onType={(v) => update({ type: v })}
            onGenre={(v) => update({ genre: v })}
            onRegion={(v) => update({ region: v })}
            onYear={(v) => update({ year: v })}
            onSort={(v) => update({ sort: v })}
            onReset={reset}
          />
        </div>

        <div className="lg:hidden flex items-center justify-between mb-6">
          <span className="text-sm text-foreground-500">共 {total} 部作品</span>
          <button
            type="button"
            onClick={() => setMobileFilter(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>
        </div>

        <div className="hidden lg:flex items-center justify-between mb-6">
          <span className="text-sm text-foreground-500">共 {total} 部作品</span>
        </div>

        {visible.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {visible.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>

            <div className="flex flex-col items-center gap-6 mt-10">
              {page < totalPages && (
                <button
                  type="button"
                  onClick={() => update({ page: String(page + 1) })}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  加载更多
                </button>
              )}
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => update({ page: String(page - 1) })}
                    className="flex items-center justify-center w-9 h-9 rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => update({ page: String(i + 1) })}
                      className={[
                        'flex items-center justify-center w-9 h-9 rounded-md text-sm transition-colors cursor-pointer',
                        page === i + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 text-foreground-600 hover:border-primary-400',
                      ].join(' ')}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => update({ page: String(page + 1) })}
                    className="flex items-center justify-center w-9 h-9 rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-inbox-line text-3xl" />
            </span>
            <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
              暂无符合条件的影视作品
            </h2>
            <p className="text-sm text-foreground-500 mb-6">试试调整筛选条件，或重置查看全部内容</p>
            <button
              type="button"
              onClick={reset}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>

      {mobileFilter && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setMobileFilter(false)}
            aria-hidden="true"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-background-50 rounded-t-2xl">
            <div className="sticky top-0 flex items-center justify-between px-4 py-4 bg-background-50 border-b border-background-200">
              <h2 className="font-heading font-bold text-lg text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setMobileFilter(false)}
                className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="px-4 py-2">
              <FilterBar
                type={type}
                genre={genre}
                region={region}
                year={year}
                sort={sort}
                onType={(v) => update({ type: v })}
                onGenre={(v) => update({ genre: v })}
                onRegion={(v) => update({ region: v })}
                onYear={(v) => update({ year: v })}
                onSort={(v) => update({ sort: v })}
                onReset={reset}
              />
            </div>
            <div className="sticky bottom-0 px-4 py-4 bg-background-50 border-t border-background-200">
              <button
                type="button"
                onClick={() => setMobileFilter(false)}
                className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer"
              >
                查看 {total} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}